import React, { useState } from 'react'
import styled from 'styled-components'
import { Flex, InfoIcon, Text, TextVariant } from '@mprolab/uikit'
import { Bell, Check, ChevronLeft, ChevronRight, Trash2 } from 'react-feather'
import { useNavigate } from 'react-router-dom'
import MainWrapper from 'layouts/MainWrapper'
import { StyledBiennale } from 'components'
import useToast from 'hooks/useToast'
import { describe, hrefFor, iconFor } from 'components/Header/components/NotificationBell'
import {
  useDeleteNotificationMutation,
  useDeleteReadNotificationsMutation,
  useGetNotificationsQuery,
  useMarkAllNotificationsReadMutation,
  useMarkNotificationsReadMutation
} from 'state/services/community/community'
import { useSession } from 'views/Community/components/useSession'

const PAGE_SIZE = 25

const Column = styled(Flex)`
  width: 100%;
  flex-direction: column;
  margin: 152px 0 120px;
`

const StyledTitle = styled(StyledBiennale)`
  font-size: 32px;
`

const Toolbar = styled(Flex)`
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 24px 0 16px;
  flex-wrap: wrap;
`

const Filters = styled(Flex)`
  gap: 8px;
  flex-wrap: wrap;
`

const Chip = styled.button<{ $active: boolean }>`
  padding: 8px 14px;
  border-radius: 999px;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.colors.cyan : theme.colors.gray8)};
  background: ${({ theme, $active }) => ($active ? theme.colors.cyan : 'transparent')};
  color: ${({ theme, $active }) => ($active ? theme.colors.background : theme.colors.white)};
`

const Action = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 999px;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: transparent;
  color: ${({ theme }) => theme.colors.white};
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`

const Card = styled(Flex)`
  flex-direction: column;
  width: 100%;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.background};
  padding: 8px 16px;
`

const Row = styled(Flex)<{ $unread: boolean }>`
  width: 100%;
  align-items: center;
  gap: 12px;
  padding: 14px 8px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray8};
  &:last-child {
    border-bottom: none;
  }
`

/**
 * The unread marker.
 *
 * A dot rather than a background wash: the register is a list somebody scans,
 * and a row that is merely a slightly different shade of dark is not a signal
 * anybody reads. Red for the same reason the bell's count is red — it is the
 * one thing here that is asking for attention.
 */
const UnreadDot = styled.span<{ $on: boolean }>`
  width: 8px;
  height: 8px;
  flex-shrink: 0;
  border-radius: 50%;
  background: ${({ $on }) => ($on ? '#e5484d' : 'transparent')};
`

const RowIcon = styled.span`
  flex-shrink: 0;
  display: flex;
  color: ${({ theme }) => theme.colors.gray6};
`

const Grow = styled(Flex)`
  flex: 1;
  min-width: 0;
  flex-direction: column;
`

const Muted = styled(Text)`
  opacity: 0.6;
`

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid transparent;
  background: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.gray6};
  &:hover {
    border-color: ${({ theme }) => theme.colors.gray8};
    color: ${({ theme }) => theme.colors.white};
  }
`

const Pager = styled(Flex)`
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 16px;
`

/**
 * The notification register: everything, including what has been read.
 *
 * The bell's dropdown shows the last few and is for acting on what just
 * happened. This is the history — reachable from the dropdown and from the
 * user menu, the way every other back-office surface we build is — and it is
 * the only place a person can delete anything.
 */
const Notifications: React.FC = () => {
  const navigate = useNavigate()
  const { toastError, toastSuccess } = useToast()
  const { account, accessToken, ready } = useSession()
  const session = { account: account as string, accessToken: accessToken as string }

  const [unreadOnly, setUnreadOnly] = useState(false)
  const [page, setPage] = useState(0)

  const { data, isLoading, isError } = useGetNotificationsQuery(
    { ...session, skip: page * PAGE_SIZE, limit: PAGE_SIZE, unreadOnly },
    { skip: !ready }
  )
  const [markRead] = useMarkNotificationsReadMutation()
  const [markAllRead, { isLoading: markingAll }] = useMarkAllNotificationsReadMutation()
  const [remove] = useDeleteNotificationMutation()
  const [removeRead, { isLoading: clearing }] = useDeleteReadNotificationsMutation()

  const rows = data?.notifications ?? []
  const total = data?.total ?? 0
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const hasRead = rows.some((n) => n.readAt)

  const run = async (fn: () => Promise<unknown>, ok: string, bad: string) => {
    try {
      await fn()
      toastSuccess(ok)
    } catch (error) {
      const message = (error as { data?: { message?: string } })?.data?.message
      toastError(bad, message ?? 'Try again shortly.')
    }
  }

  if (!ready) {
    return (
      <MainWrapper>
        <Column>
          <StyledTitle>Notifications</StyledTitle>
          <Muted variant={TextVariant.BODY_SMALL}>
            Sign in with your wallet to see them.
          </Muted>
        </Column>
      </MainWrapper>
    )
  }

  return (
    <MainWrapper>
      <Column>
        <StyledTitle>Notifications</StyledTitle>

        <Toolbar>
          <Filters>
            <Chip
              $active={!unreadOnly}
              onClick={() => {
                setUnreadOnly(false)
                setPage(0)
              }}
            >
              All
            </Chip>
            <Chip
              $active={unreadOnly}
              onClick={() => {
                setUnreadOnly(true)
                setPage(0)
              }}
            >
              Unread
            </Chip>
          </Filters>
          <Filters>
            <Action
              disabled={markingAll || rows.every((n) => n.readAt)}
              onClick={() =>
                run(
                  () => markAllRead(session).unwrap(),
                  'Marked as read',
                  'Could not mark them read'
                )
              }
            >
              <Check size={15} /> Mark all read
            </Action>
            {/*
              Clearing removes read rows only. The label carries the verb and
              the "i" carries the rule — a button that tidies up must never be
              a way to lose something unseen, and the person has to know that
              before they press it.
            */}
            <Action
              disabled={clearing || !hasRead}
              onClick={() =>
                run(
                  async () => {
                    await removeRead(session).unwrap()
                    setPage(0)
                  },
                  'Read notifications cleared',
                  'Could not clear them'
                )
              }
            >
              <Trash2 size={15} /> Clear read
            </Action>
            <InfoIcon
              iconProps={{ size: 15 }}
              tooltipProps={{
                title:
                  'Removes the notifications you have already read. Anything still unread is kept.'
              }}
            />
          </Filters>
        </Toolbar>

        <Card>
          {isLoading && <Muted variant={TextVariant.BODY_SMALL}>Loading…</Muted>}
          {/*
            Three states, not two: a list that failed to load must not be
            rendered as a person with no notifications.
          */}
          {!isLoading && isError && (
            <Muted variant={TextVariant.BODY_SMALL}>
              Could not load your notifications just now.
            </Muted>
          )}
          {!isLoading && !isError && rows.length === 0 && (
            <Muted variant={TextVariant.BODY_SMALL}>
              {unreadOnly ? 'Nothing unread.' : 'Nothing yet.'}
            </Muted>
          )}
          {rows.map((n) => (
            <Row key={n._id} $unread={!n.readAt}>
              <UnreadDot $on={!n.readAt} />
              <RowIcon>{iconFor(n.kind)}</RowIcon>
              {/*
                The row is the link. In the register they were inert — the
                bell's rows navigated and these did not, so the page that holds
                everything, including what you have already read, was the one
                place you could not act from.
              */}
              <Grow
                role="link"
                tabIndex={0}
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  if (!n.readAt) markRead({ ...session, ids: [n._id] })
                  navigate(hrefFor(n))
                }}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter' && e.key !== ' ') return
                  if (!n.readAt) markRead({ ...session, ids: [n._id] })
                  navigate(hrefFor(n))
                }}
              >
                <Text variant={TextVariant.BODY_SMALL}>{describe(n)}</Text>
                <Muted variant={TextVariant.BODY_SMALL}>
                  {new Date(n.createdAt).toLocaleString()}
                </Muted>
              </Grow>
              {!n.readAt && (
                <IconButton
                  title="Mark as read"
                  onClick={() => markRead({ ...session, ids: [n._id] })}
                >
                  <Check size={16} />
                </IconButton>
              )}
              <IconButton
                title="Delete"
                onClick={() =>
                  run(
                    () => remove({ ...session, id: n._id }).unwrap(),
                    'Deleted',
                    'Could not delete it'
                  )
                }
              >
                <Trash2 size={16} />
              </IconButton>
            </Row>
          ))}
        </Card>

        {total > PAGE_SIZE && (
          <Pager>
            <Action disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
              <ChevronLeft size={15} /> Newer
            </Action>
            <Muted variant={TextVariant.BODY_SMALL}>
              Page {page + 1} of {pages} · {total} in total
            </Muted>
            <Action
              disabled={page + 1 >= pages}
              onClick={() => setPage((p) => p + 1)}
            >
              Older <ChevronRight size={15} />
            </Action>
          </Pager>
        )}

        <Flex style={{ marginTop: 20 }}>
          <Action onClick={() => navigate('/community?tab=clans')}>
            <Bell size={15} /> Community
          </Action>
        </Flex>
      </Column>
    </MainWrapper>
  )
}

export default Notifications
